import { Outlet } from 'react-router-dom';

import { Illustration } from '../components/atoms/illustration/illustration';

import { BackgroundConfig, useBackground } from './background-context';

type BackgroundVariant = 'purpleGreen' | 'indigoPurple';

const backgroundVariants: Record<BackgroundVariant, { start: string; end: string }> = {
  purpleGreen: { start: '#25F4CE', end: '#5A7BFB' },
  indigoPurple: { start: '#4166FB', end: '#D19AF9' },
};

const DEFAULT_VARIANT = 'purpleGreen';

const toGradientStyle = (v: BackgroundVariant): React.CSSProperties => {
  const selected = backgroundVariants[v];
  return {
    '--ellipse-grad-start': selected.start,
    '--ellipse-grad-end': selected.end,
  } as React.CSSProperties;
};

type BackgroundProps = {
  readonly ellipse1Style: React.CSSProperties;
  readonly ellipse2Style: React.CSSProperties;
  readonly opacity?: string;
};

const DesktopBackground = ({ ellipse1Style, ellipse2Style, opacity }: BackgroundProps) => (
  <>
    <Illustration
      className="fixed top-0 right-0 z-0 h-[70vh] w-auto"
      name="Ellipse1Desktop"
      style={{ ...ellipse1Style, opacity }}
    />
    <Illustration
      className="fixed bottom-0 left-0 z-0 h-[70vh] w-auto"
      name="Ellipse2Desktop"
      style={{ ...ellipse2Style, opacity }}
    />
  </>
);

const MobileBackground = ({ ellipse1Style, ellipse2Style, opacity }: BackgroundProps) => (
  <>
    <Illustration
      className="fixed top-0 right-0 z-0 w-[50vw] h-auto md:w-[40vw] md:h-auto"
      name="Ellipse1Mobile"
      style={{ ...ellipse1Style, opacity }}
    />
    <Illustration
      className="fixed bottom-0 left-0 z-0 w-[65vw] h-auto md:w-[50vw] md:h-auto"
      name="Ellipse2Mobile"
      style={{ ...ellipse2Style, opacity }}
    />
  </>
);

const Background = ({ visible, variant, ellipse1Variant, ellipse2Variant, opacity }: BackgroundConfig) => {
  const ellipse1Style = toGradientStyle(ellipse1Variant ?? variant ?? DEFAULT_VARIANT);
  const ellipse2Style = toGradientStyle(ellipse2Variant ?? variant ?? DEFAULT_VARIANT);

  return (
    visible && (
      <div className="relative h-full w-full overflow-hidden">
        <div className="hidden lg:flex">
          <DesktopBackground ellipse1Style={ellipse1Style} ellipse2Style={ellipse2Style} opacity={opacity} />
        </div>
        <div className="flex lg:hidden">
          <MobileBackground ellipse1Style={ellipse1Style} ellipse2Style={ellipse2Style} opacity={opacity} />
        </div>
      </div>
    )
  );
};

const BackgroundLayout = () => {
  const { config } = useBackground();

  return (
    <div className="relative h-full w-full">
      <div className="z-0">
        <Background {...config} />
      </div>
      <div className="relative z-10 h-full w-full">
        <Outlet />
      </div>
    </div>
  );
};

export { BackgroundLayout };
export type { BackgroundVariant };
